import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Calculator, Euro, TrendingUp, TrendingDown, Info } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContributionSettings = () => {
  const [simulating, setSimulating] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [grossSalary, setGrossSalary] = useState(1500);
  const [allowances, setAllowances] = useState(50);
  const [hoursPerDay, setHoursPerDay] = useState(9);
  
  const handleSimulate = async () => {
    try {
      setSimulating(true);
      const res = await axios.post(`${API}/simulator/calculate`, {
        gross_salary: grossSalary,
        allowances: allowances,
        hours_per_day: hoursPerDay,
      });
      
      // Vérifier si la réponse contient une erreur métier
      if (res.data.error) {
        toast.error(res.data.error + (res.data.detail ? ` : ${res.data.detail}` : ''));
        return;
      }
      
      setSimulation(res.data.simulation);
    } catch (error) {
      console.error('Error simulating:', error);
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.error
        || error.message 
        || 'Erreur lors de la simulation';
      toast.error(errorMessage);
    } finally {
      setSimulating(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-secondary)' }}>
          Simulateur de Paie
        </h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Calculez le salaire net et les cotisations sociales d'une assistante maternelle
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Formulaire de simulation */}
        <Card data-testid="simulation-form">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" style={{ color: 'var(--primary-500)' }} />
              Paramètres de simulation
            </CardTitle>
            <CardDescription>
              Saisissez les informations pour calculer le salaire net
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="gross-salary">Salaire brut mensuel</Label>
              <div className="relative">
                <Input
                  id="gross-salary"
                  type="number"
                  step="0.01"
                  min="0"
                  value={grossSalary}
                  onChange={(e) => setGrossSalary(parseFloat(e.target.value) || 0)}
                  className="pr-8"
                  data-testid="input-gross-salary"
                />
                <Euro className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-tertiary)]" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="allowances">Indemnités et primes</Label>
              <div className="relative">
                <Input
                  id="allowances"
                  type="number"
                  step="0.01"
                  min="0"
                  value={allowances}
                  onChange={(e) => setAllowances(parseFloat(e.target.value) || 0)}
                  className="pr-8"
                  data-testid="input-allowances"
                />
                <Euro className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-tertiary)]" />
              </div>
              <p className="text-xs text-[var(--text-tertiary)]">
                Heures supplémentaires, prime exceptionnelle, etc.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hours-per-day">Heures de garde par jour</Label>
              <Input
                id="hours-per-day"
                type="number"
                step="0.5"
                min="0"
                max="24"
                value={hoursPerDay}
                onChange={(e) => setHoursPerDay(parseFloat(e.target.value) || 0)}
                data-testid="input-hours"
              />
              <p className="text-xs text-[var(--text-tertiary)]">
                Pour le calcul de l'indemnité d'entretien
              </p>
            </div>

            <Separator className="my-4" />

            <Button
              onClick={handleSimulate}
              disabled={simulating || grossSalary <= 0}
              className="w-full"
              data-testid="btn-simulate"
            >
              <Calculator className="h-4 w-4 mr-2" />
              {simulating ? 'Calcul en cours...' : 'Simuler'}
            </Button>
          </CardContent>
        </Card>

        {/* Résultats de simulation */}
        <Card data-testid="simulation-results">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5" style={{ color: 'var(--success)' }} />
              Résultats de la simulation
            </CardTitle>
            <CardDescription>
              {simulation ? 'Détails du calcul de paie' : 'Lancez une simulation pour voir les résultats'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {simulation ? (
              <div className="space-y-6">
                {/* Montants principaux */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Salaire brut</span>
                    <span className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {simulation.gross_salary.toFixed(2)} €
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-2 bg-[var(--error-light)] px-3 rounded">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="h-4 w-4" style={{ color: 'var(--error)' }} />
                      <span className="text-sm font-medium" style={{ color: 'var(--error)' }}>Cotisations salariales</span>
                    </div>
                    <span className="text-base font-semibold" style={{ color: 'var(--error)' }}>
                      - {simulation.employee_contributions.toFixed(2)} €
                    </span>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between py-2">
                    <span className="text-base font-medium" style={{ color: 'var(--text-primary)' }}>Salaire net</span>
                    <span className="text-2xl font-bold" style={{ color: 'var(--success)' }}>
                      {simulation.net_salary.toFixed(2)} €
                    </span>
                  </div>

                  {simulation.allowances > 0 && (
                    <div className="flex items-center justify-between py-2 text-sm">
                      <span style={{ color: 'var(--text-secondary)' }}>+ Indemnités</span>
                      <span style={{ color: 'var(--text-primary)' }}>{simulation.allowances.toFixed(2)} €</span>
                    </div>
                  )}

                  {simulation.indemnite_entretien > 0 && (
                    <div className="flex items-center justify-between py-2 text-sm">
                      <span style={{ color: 'var(--text-secondary)' }}>+ Indemnité d'entretien</span>
                      <span style={{ color: 'var(--text-primary)' }}>{simulation.indemnite_entretien.toFixed(2)} €</span>
                    </div>
                  )}

                  <Separator />

                  <div className="flex items-center justify-between py-2 bg-[var(--success-light)] px-3 rounded">
                    <span className="text-base font-semibold" style={{ color: 'var(--success)' }}>Net à payer</span>
                    <span className="text-2xl font-bold" style={{ color: 'var(--success)' }}>
                      {simulation.net_to_pay.toFixed(2)} €
                    </span>
                  </div>
                </div>

                <Separator />

                {/* Coût employeur */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Salaire brut</span>
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                      {simulation.gross_salary.toFixed(2)} €
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-2 bg-[var(--warning-light)] px-3 rounded">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" style={{ color: 'var(--warning)' }} />
                      <span className="text-sm font-medium" style={{ color: 'var(--warning-dark)' }}>Cotisations patronales</span>
                    </div>
                    <span className="text-base font-semibold" style={{ color: 'var(--warning-dark)' }}>
                      + {simulation.employer_contributions.toFixed(2)} €
                    </span>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between py-2 bg-[var(--primary-50)] px-3 rounded">
                    <span className="text-base font-semibold" style={{ color: 'var(--primary-600)' }}>Coût total employeur</span>
                    <span className="text-2xl font-bold" style={{ color: 'var(--primary-600)' }}>
                      {simulation.total_cost.toFixed(2)} €
                    </span>
                  </div>
                </div>

                {/* Info version */}
                <div className="flex items-start gap-2 p-3 rounded bg-[var(--neutral-50)]">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
                  <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    Calcul basé sur les taux de cotisations sociales 2026 (fichier de configuration).
                    Les taux sont mis à jour annuellement selon les publications officielles de l'Urssaf et Pajemploi.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Calculator className="h-12 w-12 mb-4" style={{ color: 'var(--neutral-300)' }} />
                <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                  Remplissez les paramètres et cliquez sur "Simuler" pour voir les résultats
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContributionSettings;
